import { FC, useContext, useState } from "react";
import ExceptionsFiltersContextProvider, { ExceptionsFiltersContext } from "@app/exceptions/contexts/ExceptionsFiltersContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import ExceptionsDownload from "@app/exceptions/ExceptionsDownload";
import ExceptionsDownloadAllModal from "@app/exceptions/ExceptionsDownloadAllModal";
import GenerateDocumentFlyout from "@app/exceptions/GenerateDocumentFlyout";
import { ExceptionDocumentType } from "@app/exceptions/enums";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { Link } from "react-router-dom";
import AddAttachmentModal from "@app/exceptions/AddAttachmentModal";
import ArchiveExceptionsModal from "@app/exceptions/ArchiveExceptionsModal";
import ExpireModal from "@app/exceptions/ExpireModal";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ExceptionsOverview } from "@app/exceptions/ExceptionsOverview";
import UnprocessedExceptionsBanner from "@app/exceptions/UnprocessedExceptionsBanner";
import EditExceptionFlyout from "@app/exceptions/EditExceptionFlyout";
import { clone } from "lodash";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";

type TabViews = "Overview" | "Downloads" | "Archived";

const WrappedExceptionsHome: FC = () => {

  const title = "Exceptions";
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const exceptionFilterContext = useContext(ExceptionsFiltersContext);
  const [activeTab, setActiveTab] = useState<TabViews>(exceptionFilterContext.isForArchived ? "Archived" : "Overview");
  const [isDownloadAllModalOpen, setIsDownloadAllModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isAddAttachmentModalOpen, setIsAddAttachmentModalOpen] = useState(false);
  const [isExpireModalOpen, setIsExpireModalOpen] = useState(false);
  const [enableMoreActionsButton, setEnableMoreActionsButton] = useState(true);
  const selectedIsCciioFlags = exceptionFilterContext.selectedIsCciioFlags;
  const hasMixedLOBTypes = selectedIsCciioFlags.some((v: boolean) => v) && selectedIsCciioFlags.some((v: boolean) => !v);
  const isCciio = selectedIsCciioFlags.some((v: boolean) => v) && !hasMixedLOBTypes;
  const isCciioFlagEnabled = featureFlags.has(Features.CCIIO_JUSTIFICATION_EXPORT);
  const defaultExceptionStatusesForBulkEdit = clone(exceptionFilterContext.exceptionStatusOptions);
  //Add the "None" or blank option for the bulk edit at the start of the array
  defaultExceptionStatusesForBulkEdit.unshift({ key: "none", name: "None" });

  const handleOpenDownloadAllModal = () => {
    setIsDownloadAllModalOpen(true);
  };

  const handleAddAttachmentsModal = () => {
    setIsAddAttachmentModalOpen(true);
  };

  const clearSelectedCheckboxes = () => {
    exceptionFilterContext.setSelectedExceptionIds([]);
  };

  const handleOpenArchiveModal = () => {
    setIsArchiveModalOpen(true);
  }

  const handleOpenExpireModal = () => {
    setIsExpireModalOpen(true);
  };

  const setTab = (tabName: string) => {
    if (tabName == "Downloads") {
      setEnableMoreActionsButton(false);
    } else {
      setEnableMoreActionsButton(true);
    }
    setActiveTab(tabName as TabViews);

    if (tabName == "Overview" && exceptionFilterContext.isForArchived
      || tabName == "Archived" && !exceptionFilterContext.isForArchived) {
      exceptionFilterContext.setTriggerRefreshData(true);
      exceptionFilterContext.setRequestModel(null);
      exceptionFilterContext.setHasSetInitialExceptionStatuses(false);
    }

    exceptionFilterContext.setPageIndex(1);
    exceptionFilterContext.setPageSize(100);
    exceptionFilterContext.setSelectedExceptionIds([]);
  }

  return (
    <Page documentTitle={title}>
      <Breadcrumbs items={[title]} />
      <PageInner>
        <PageHeader title={title} disableHR={true}>
          <PageHeaderRight>
            <div style={{ alignItems: 'center', display: 'flex', gap: '10px' }}>
              <NewActionMenu
                alignRight={true}
                buttonText="More Actions"
                containerClassnames="exceptions__more-actions-container"
                testId="more-actions-select"
                buttonClassNames="qes-button-primary"
                disabled={!enableMoreActionsButton}
              >
                {activeTab !== "Archived" &&
                  <FlyoutLink
                    classNames="action-menu-item col-12 fw-normal"
                    content={{
                      body: <GenerateDocumentFlyout
                        selectedExceptionIds={exceptionFilterContext.selectedExceptionIds}
                        resetSelectedCheckboxes={clearSelectedCheckboxes}
                        customProjectName={exceptionFilterContext.customProjectName}
                        enableRegulatoryExceptionReports={exceptionFilterContext.enableRegulatoryExceptionReports}
                        haveRemovedServiceArea={exceptionFilterContext.haveRemovedServiceArea}
                        haveRemovedSpecialty={exceptionFilterContext.haveRemovedSpecialty}
                        refreshData={() => exceptionFilterContext.setTriggerRefreshData(true)}
                        documentType={isCciioFlagEnabled && isCciio ? ExceptionDocumentType.CCIIO : ExceptionDocumentType.CMS}
                      />,
                      title: "Generate Document",
                    }}
                    size="40"
                    testId="more-actions-select__generate-document"
                    disabled={!isCciioFlagEnabled ? exceptionFilterContext.selectedExceptionIds.length === 0 : exceptionFilterContext.selectedExceptionIds.length === 0 || hasMixedLOBTypes}
                  >
                    Generate Document
                  </FlyoutLink>
                }
                {activeTab !== "Archived" &&
                  <FlyoutLink
                    classNames="action-menu-item col-12 fw-normal"
                    content={{
                      body: <EditExceptionFlyout
                        exceptionStatusOptions={defaultExceptionStatusesForBulkEdit}
                        isForBulkEdit={true}
                        isBulkCCIIO={isCciio}
                      />,
                      title: `${exceptionFilterContext.selectedExceptionIds.length} exception${exceptionFilterContext.selectedExceptionIds.length == 1 ? "" : "s"} selected`
                    }}
                    size="40"
                    testId="more-actions-select__bulk-edit"
                    disabled={exceptionFilterContext.selectedExceptionIds.length === 0}
                  >
                    Edit
                  </FlyoutLink>
                }
                <NewActionMenuItem
                  display="Download All"
                  isSelected={false}
                  key="download-all"
                  onClick={handleOpenDownloadAllModal}
                  testId="more-actions-select__download-all"
                />
                {activeTab !== "Archived" &&
                  <NewActionMenuItem
                    display="Add Attachments"
                    isSelected={exceptionFilterContext.selectedExceptionIds.length === 0}
                    key="add-attachments"
                    onClick={handleAddAttachmentsModal}
                    testId="more-actions-select__add-attachments"
                  />
                }
                {activeTab !== "Archived" &&
                  <NewActionMenuItem
                    display="Expire"
                    isSelected={exceptionFilterContext.selectedExceptionIds.length === 0}
                    key="expire"
                    onClick={handleOpenExpireModal}
                    testId="more-actions-select__expire"
                  />
                }
                {activeTab !== "Archived" &&
                  <NewActionMenuItem
                    display="Archive"
                    isSelected={exceptionFilterContext.selectedExceptionIds.length === 0}
                    key="archive"
                    onClick={handleOpenArchiveModal}
                    testId="more-actions-select__archive"
                    aria-disabled={true}
                  />
                }
              </NewActionMenu>
              <NewActionMenu
                alignRight={true}
                buttonText="Add Exception"
                testId="add-exception"
                buttonClassNames="qes-button-primary"
              >
                <Link to={"./create"}>
                  <NewActionMenuItem
                    display="Create New"
                    isSelected={false}
                    key="create-new"
                    onClick={() => { }}
                    testId="add-exception__create-new"
                  />
                </Link>

                <Link to={"./bulk-upload"}>
                  <NewActionMenuItem
                    display="Upload File"
                    isSelected={false}
                    key="bulk-upload"
                    onClick={() => { }}
                    testId="add-exception__bulk-upload"
                  />
                </Link>
              </NewActionMenu>
            </div>
            <ExceptionsDownloadAllModal
              isModalOpen={isDownloadAllModalOpen}
              setIsModalOpen={setIsDownloadAllModalOpen}
            />
            <AddAttachmentModal
              isModalOpen={isAddAttachmentModalOpen}
              setIsModalOpen={setIsAddAttachmentModalOpen}
              selectedExceptionIds={exceptionFilterContext.selectedExceptionIds}
              resetSelectedCheckboxes={clearSelectedCheckboxes}
            />
            <ExpireModal
              isModalOpen={isExpireModalOpen}
              setIsModalOpen={setIsExpireModalOpen}
              resetSelectedCheckboxes={clearSelectedCheckboxes}
            />
            <ArchiveExceptionsModal
              isModalOpen={isArchiveModalOpen}
              setIsModalOpen={setIsArchiveModalOpen}
              resetSelectedCheckboxes={clearSelectedCheckboxes}
            />
          </PageHeaderRight>

            </PageHeader>
            <PageSection>
              <div className="tabs-style-2 mb-3">
                <ul className="nav nav-tabs" role="tablist">
                  <li>
                    <QESButton
                      qesType="none"
                      className={`nav-link text-dark fw-normal ${activeTab === "Overview" ? "active disabled" : ""}`}
                      qesTestId="exception-nav__tab--overview"
                      onClick={() => setTab("Overview" as TabViews)}
                      aria-disabled={activeTab === "Overview"}
                      role="tab"
                    >
                      Overview
                    </QESButton>
                  </li>
                  <li>
                    <QESButton
                      qesType="none"
                      className={`nav-link text-dark fw-normal ${activeTab === "Downloads" ? "active disabled" : ""}`}
                      qesTestId="exception-nav__tab--downloads"
                      onClick={() => setTab("Downloads" as TabViews)}
                      aria-disabled={activeTab === "Downloads"}
                      role="tab"
                    >
                      Downloads
                    </QESButton>
                  </li>
                  <li>
                    <QESButton
                      qesType="none"
                      className={`nav-link text-dark fw-normal ${activeTab === "Archived" ? "active disabled" : ""}`}
                      qesTestId="exception-nav__tab--archived"
                      onClick={() => setTab("Archived" as TabViews)}
                      aria-disabled={activeTab === "Archived"}
                      role="tab"
                    >
                      Archived
                    </QESButton>
                  </li>
                </ul>
              </div>
            </PageSection>

        {activeTab === "Overview" &&
          <>
            <UnprocessedExceptionsBanner />
            <ExceptionsOverview isArchivedTable={false} />
          </>
        }
        {activeTab === "Downloads" &&
          <ExceptionsDownload />
        }
        {activeTab === "Archived" &&
          <ExceptionsOverview isArchivedTable={true} />
        }
      </PageInner>
    </Page>
  );
};

const ExceptionsHome: FC = () => (
  <ExceptionsFiltersContextProvider>
    <WrappedExceptionsHome />
  </ExceptionsFiltersContextProvider>
);

export default ExceptionsHome;
